import React, { Dispatch, SetStateAction } from "react";
import { Query, QueryResult } from "react-apollo";
import gql from "graphql-tag";
import Select from "react-select";
import { Text, FormControl, FormLabel, Box, Link } from "@chakra-ui/react";
import { InfoOutlineIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { SelectOption, SelectedOption } from "./AssignsFavorites";
import useDebounce from "../../lib/hooks/useDebounce";

const AdminFolderSearchQuery = gql`
  query SalesAdminFolderQuery(
    $limit: Int
    $offset: Int!
    $searchKeyword: String
  ) {
    salesAdminFolders(
      limit: $limit
      offset: $offset
      searchKeyword: $searchKeyword
    ) {
      name
      id
      user {
        id
        name
      }
    }
  }
`;

interface SalesAdminFolderSelectProps {
  onFolderChange: Dispatch<SetStateAction<SelectedOption | null>>;
  selectedFolder: SelectedOption;
}

interface Folder {
  id: string;
  name: string;
  user: {
    id: string;
    name: string;
  };
}

interface FolderOptions {
  label: string;
  options: SelectOption[];
}

interface SalesAdminFolderResults {
  salesAdminFolders: Folder[];
}

interface SalesAdminFolderVariables {
  limit?: number;
  offset: number;
}

const formatSalesAdminFolders = (folders: Folder[]) => {
  const userFolders: {
    [userId: string]: FolderOptions;
  } = {};

  if (!folders?.length) return [];

  const result = folders.reduce<FolderOptions[]>((acc, current) => {
    const userId = current.user.id;
    if (!userFolders[userId]) {
      userFolders[userId] = {
        label: current.user.name,
        options: [],
      };
      acc.push(userFolders[userId]);
    }
    userFolders[userId].options.push({
      value: current.id,
      label: current.name,
    });
    return acc;
  }, []);

  return result;
};

const SalesAdminFolderSelect = ({
  onFolderChange,
  selectedFolder,
}: SalesAdminFolderSelectProps): JSX.Element => {
  const {
    value: searchKeyword,
    deferredValue: deferredKeyword,
    setValue: setKeyword,
  } = useDebounce(250);

  return (
    <Query
      query={AdminFolderSearchQuery}
      variables={{
        offset: 0,
        searchKeyword: deferredKeyword,
      }}
    >
      {({
        data,
        error,
        fetchMore,
      }: QueryResult<SalesAdminFolderResults, SalesAdminFolderVariables>) => {
        if (error) return <Text>Error while getting folders</Text>;

        if (data?.salesAdminFolders) {
          const groupedFolders = formatSalesAdminFolders(
            data.salesAdminFolders
          );
          return (
            <FormControl mb={[8, 8, 0]}>
              <FormLabel htmlFor="email">Select Folder</FormLabel>
              <Select
                id="admin-folder-select"
                value={selectedFolder}
                inputValue={searchKeyword}
                onInputChange={(value) => {
                  setKeyword(value);
                }}
                classNamePrefix="folder-select"
                styles={{
                  control: (styles) => ({ ...styles, height: "45px" }),
                }}
                menuPortalTarget={document.querySelector("body")}
                isClearable={true}
                onChange={(folder) => onFolderChange(folder)}
                placeholder="Search folder by name..."
                options={groupedFolders}
                onMenuScrollToBottom={() => {
                  fetchMore({
                    variables: { offset: data.salesAdminFolders.length },
                    updateQuery: (prev, { fetchMoreResult }) => {
                      if (!fetchMoreResult) {
                        return prev;
                      }

                      return {
                        salesAdminFolders: [
                          ...prev.salesAdminFolders,
                          ...fetchMoreResult.salesAdminFolders,
                        ],
                      };
                    },
                  });
                }}
              />
              <Box
                fontSize={"sm"}
                mt={1}
                justifyItems={"center"}
                alignItems={"center"}
                color="gray.500"
              >
                <Text m={0} display={"inline-block"}>
                  <InfoOutlineIcon color="#2F87D0" mr={1} pb={"2px"} />
                  {`Create new favorites folder in the favorites page on the`}
                  <Link ml={1} color="#2F87D0" href={"/favorites"}>
                    web app <ExternalLinkIcon pb={"2px"} />
                  </Link>
                </Text>
              </Box>
            </FormControl>
          );
        }
        return null;
      }}
    </Query>
  );
};

export default SalesAdminFolderSelect;
